/** @typedef {import('headlight-configurable-application').Pict} Pict */
/** @typedef {import('headlight-configurable-application').Fable} Fable */

const libPictView = require('pict-view');

const libTestResultsSummary = require('../providers/_TestResultsSummary.js');

const SUMMARY_ELEMENT_ID = 'Headlight-View-TestResultsSummary';

// Remarks report every keystroke; redraw once typing pauses.
const REFRESH_DEBOUNCE_MS = 150;

/**
 * Read-only test results summary drawn between the form header and the form body, on screen and in the PDF.
 * It stays hidden until a test has a result.
 *
 * `SampleTestAcceptance` asks for a refresh whenever it marshals or changes, and the RichText provider when a
 * test remark changes; the refresh is debounced so a full-form marshal draws once.
 */
class TestResultsSummary extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict = pFable;
		/** @type {Fable} */
		this.fable = pFable;

		/** @type {Record<string, any>|null} */
		this._marshalDestination = null;
		/** @type {ReturnType<typeof setTimeout>|null} */
		this._refreshTimer = null;
	}

	/**
	 * Schedule a redraw from the given form data.
	 *
	 * @param {Record<string, any>} pMarshalDestination - The form data the acceptance inputs read from.
	 */
	requestRefresh(pMarshalDestination)
	{
		if (pMarshalDestination && typeof(pMarshalDestination) === 'object')
		{
			this._marshalDestination = pMarshalDestination;
		}
		if (this._refreshTimer)
		{
			clearTimeout(this._refreshTimer);
			this._refreshTimer = null;
		}
		// The PDF rasterizer prints as soon as the form reports ready, so a printed render draws now.
		if (this.pict.AppData && this.pict.AppData.Interactive === false)
		{
			this.refreshSummary();
			return;
		}
		this._refreshTimer = setTimeout(() =>
		{
			this._refreshTimer = null;
			this.refreshSummary();
		}, REFRESH_DEBOUNCE_MS);
	}

	/**
	 * Redraw now.
	 */
	refreshSummary()
	{
		if (typeof(document) === 'undefined' || !this._marshalDestination)
		{
			return;
		}
		const tmpHost = this._getHostElement();
		if (!tmpHost)
		{
			return;
		}
		tmpHost.replaceChildren();

		const tmpManifest = this.pict.views.PictFormMetacontroller?.manifestDescription;
		const tmpAcceptance = this.pict.providers.SampleTestAcceptance;
		const tmpRows = libTestResultsSummary.collectTestResults(
			{
				Descriptors: tmpManifest?.Descriptors || {},
				Sections: tmpManifest?.Sections || [],
				Data: this._marshalDestination,
				getValueAtAddress: (pObject, pAddress) => this.pict.manifest.getValueAtAddress(pObject, pAddress),
				// @ts-ignore - provider-private resolver, reused so the summary reads the same remarks the input shows
				getRemarksAddress: tmpAcceptance && tmpAcceptance._getRemarksAddress ? (pInput) => tmpAcceptance._getRemarksAddress(pInput, null) : null,
				Document: document,
			});
		if (!libTestResultsSummary.hasAnyResult(tmpRows))
		{
			tmpHost.classList.add('is-hidden');
			return;
		}
		tmpHost.append(libTestResultsSummary.buildSummaryElement(document, tmpRows));
		tmpHost.classList.remove('is-hidden');
	}

	/**
	 * The summary's own element, created after the form header (or before the form body) on first use.
	 *
	 * @return {HTMLElement|null}
	 */
	_getHostElement()
	{
		const tmpExisting = document.getElementById(SUMMARY_ELEMENT_ID);
		if (tmpExisting)
		{
			return tmpExisting;
		}
		const tmpHeader = document.getElementById('Headlight-View-Header');
		const tmpFormContainer = document.getElementById('Pict-Form-Container');
		if (!tmpHeader && !tmpFormContainer)
		{
			return null;
		}
		const tmpHost = document.createElement('div');
		tmpHost.id = SUMMARY_ELEMENT_ID;
		tmpHost.className = 'is-hidden';
		if (tmpHeader)
		{
			tmpHeader.after(tmpHost);
		}
		else
		{
			tmpFormContainer.before(tmpHost);
		}
		return tmpHost;
	}
}

module.exports = TestResultsSummary;

module.exports.default_configuration =
{
	ViewIdentifier: 'TestResultsSummary',
	AutoRender: false,
	CSS: /*css*/`
#Headlight-View-TestResultsSummary .test-results-summary { margin: 0.75rem 0 1.25rem; padding: 0.75rem 1rem; }
#Headlight-View-TestResultsSummary .test-results-summary-heading { display: flex; align-items: baseline; justify-content: space-between; gap: 1rem; margin-bottom: 0.5rem; }
#Headlight-View-TestResultsSummary .table { background-color: transparent; }
#Headlight-View-TestResultsSummary .test-results-summary-result { white-space: nowrap; }
#Headlight-View-TestResultsSummary .test-results-summary-remarks { max-width: 40ch; }
`,
	Templates: [],
	Renderables: [],
};
